import { useCallback, useEffect, useRef, useState } from 'react';

/** Shell width at which the chat switches to its wide layouts. */
export const WIDE_SHELL_MIN_WIDTH = 640;

/**
 * Track the bound element's content-box inline-size via ResizeObserver.
 * Returns a `ref` to attach to the observed element and the latest
 * `width` in CSS px (initially `null` until the first observation).
 *
 * We use this instead of `@container inline-size` CSS queries because
 * container-type interacts unpredictably with `display: flex` on some
 * combinations (notably when a flex item has no explicit width), and
 * we'd rather have one source of truth driving both layout decisions
 * (side panel vs drawer) and downstream sizing (blog carousel cells).
 */
export function useShellWidth(): {
  ref: (node: HTMLElement | null) => void;
  width: number | null;
} {
  const [width, setWidth] = useState<number | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (!node || typeof ResizeObserver === 'undefined') {
      if (node) {
        // No ResizeObserver — fall back to one-shot measurement.
        setWidth(node.getBoundingClientRect().width);
      }
      return;
    }
    setWidth(node.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Prefer content-box inline-size when available; fall back to
        // boundingClientRect.width for older Safari versions.
        const cb = entry.contentBoxSize?.[0]?.inlineSize;
        const next = typeof cb === 'number' ? cb : entry.contentRect.width;
        setWidth(next);
      }
    });
    ro.observe(node);
    roRef.current = ro;
  }, []);

  useEffect(() => {
    return () => {
      roRef.current?.disconnect();
      roRef.current = null;
    };
  }, []);

  return { ref, width };
}
